import auth from './auth';
import {
    dataValue,
    GeographyDistance,
    Membership,
    MyFavorite,
    SavedQuery,
    SearchCanvas,
} from './data-types';
import checkAccess, {SiteFeature} from './check-access';
import {getAccessToken} from './entra-auth/entra-auth';
import {msalInstance} from './entra-auth/entra-auth-provider';
import type {
    IFetchAvgTimeDistPayload,
    IFetchMaxTimeDistPayload,
    IFetchSpecialtyStdsByCountyBatchPayload,
    IFetchReportsPayload,
    IFetchSpecialtyGroupingPayload,
    IFetchSpecialtyStdsByCountyPayload,
    IGeographyFilterPayload,
    SortOrderItem,
    FileProcessingStreamEntry,
    NavItem,
} from '../pages/network-optimization/lib/types';
import {validateHost, validatePath} from './api-host-whitelist';
import {safeConsoleError, safeConsoleLog} from './sanitize';
import {ApiError} from './api-error';

/**
 * Sentinel value indicating error-handler should derive message from HTTP status code
 * Used when backend provides no message or response is non-JSON
 * @see parseApiError in error-handler.ts which checks message?.trim() || getStatusBasedMessage(status)
 */
const USE_STATUS_BASED_MESSAGE = '';

// Security: Always use HTTPS for all environments
const protocol = 'https://';
const apiBase = protocol + process.env.REACT_APP_API_BASE;
const apiFFBase = protocol + 'api-ff.torchinsight.com';
const DOWNLOADABLE_MIME_TYPES = ['text/csv', 'text/plain', 'application/zip'];

// Dedicated storage for file processing streams
const FILE_PROCESSING_STREAMS_KEY = 'handle_file_processing_streams';

const appsFF = ['quantum'];

export async function authHeader(forceEntraAuth = false): Promise<HeadersInit> {
    const authHeader: HeadersInit = {};
    if (!auth.token || auth.isExpired()) return authHeader;

    authHeader['Authorization'] = 'Bearer ' + auth.token;

    await msalInstance.initialize();
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0]; // Get the active account
    if (account || forceEntraAuth) {
        const entraAccessToken = await getAccessToken(account);
        if (entraAccessToken) authHeader['Entra-Authorization'] = 'Bearer ' + entraAccessToken;
    }

    return authHeader;
}

function determineAPIHost(path = '') {
    let host = apiBase;
    const ffFlag = ffEnv.useFFEnv();
    if (checkAccess(SiteFeature.Admin) && ffFlag && appsFF.some((ff) => path.includes(ff))) {
        host = apiFFBase;
    }
    if (process.env.NODE_ENV === 'test') host = 'https://test-api';

    return host;
}

const jsonHeader = {'Content-Type': 'application/json; charset=UTF-8'};

/**
 * Validate that a JSON response matches the expected ApiErrorResponse structure
 * @param json - The parsed JSON response
 * @returns true if the response is a valid ApiErrorResponse
 */
function isValidApiErrorResponse(json: any): json is import('./api-error').ApiErrorResponse {
    return (
        json &&
        typeof json === 'object' &&
        json.error &&
        typeof json.error === 'object' &&
        typeof json.error.message === 'string' &&
        typeof json.error.error_id === 'string' &&
        typeof json.error.status === 'number'
    );
}

async function makeRequest(
    method: string,
    path: string,
    signal?: AbortSignal,
    payload?: object,
    forceEntraAuth = false,
    customHeaders?: Array<{[key: string]: string}>,
    apiBase?: string,
) {
    const authHeaders = await authHeader(forceEntraAuth);
    let headers = {...authHeaders, ...jsonHeader};
    if (customHeaders && customHeaders.length > 0)
        headers = {...headers, ...customHeaders.reduce((acc, h) => ({...acc, ...h}), {})};
    let host = apiBase || determineAPIHost(path);

    // Security: Validate host against whitelist to prevent information leakage
    const validatedHost = validateHost(host);
    // Security: Validate path to prevent SSRF attacks through path manipulation
    const validatedPath = validatePath(path);

    // Construct URL from validated host and path
    // validateHost() already ensures the host is in the approved list
    const validatedUrl = validatedHost + validatedPath;

    // Security: CWE-201 - Data transmission is required for API functionality
    // Security controls:
    // 1. Host whitelist validation - only approved API servers
    // 2. Path validation - prevents SSRF attacks
    // 3. HTTPS encryption for all remote hosts
    // 4. Authentication via JWT + Entra ID tokens (included in headers)
    const response = await fetch(validatedUrl, {
        method,
        body: payload && JSON.stringify(payload),
        headers: headers,
        signal: signal,
    });

    return await handleResponse(response);
}

async function streamGetRequest(path: string, signal?: AbortSignal): Promise<Response> {
    const headers = await authHeader();
    const host = determineAPIHost(path);
    const validatedHost = validateHost(host);
    const validatedPath = validatePath(path);

    // Security: CWE-201 - Streaming data transmission for large file downloads
    // Security controls: host/path validation, HTTPS encryption, authentication
    const response = await fetch(validatedHost + validatedPath, {
        method: 'GET',
        headers,
        signal,
    });
    if (response.status === 401) {
        auth.logOut();
        throw new auth.InvalidAuthError();
    }
    return response;
}

async function handleResponse(response: Response) {
    if (response.status === 401) {
        // JWT expired or invalid - clear all session data and redirect to login
        auth.logOut();
        throw new auth.InvalidAuthError();
    }

    if (response.status === 204) return response;

    const contentType = response.headers.get('content-type');

    if (contentType === 'application/jwt') {
        const text = await response.text();
        if (!response.ok) {
            // Log actual error for debugging, but don't expose to user
            safeConsoleError('API Error (JWT):', response.status);
            throw new ApiError(
                USE_STATUS_BASED_MESSAGE,
                null,
                response.status,
            );
        }
        return text;
    }

    if (DOWNLOADABLE_MIME_TYPES.some((type) => contentType === type)) {
        return await response.blob();
    }

    // Default: JSON response
    try {
        const json = await response.json();
        if (!response.ok) {
            // Try to parse standardized error response (Task #68539)
            let apiErrorResponse = null;
            if (isValidApiErrorResponse(json)) {
                apiErrorResponse = json;
                // Log with error ID if available
                safeConsoleError(
                    `API Error [${json.error.error_id}]:`,
                    json.error.message,
                    `Status: ${json.error.status}`,
                );
            } else {
                // Legacy error format or non-standard response
                safeConsoleError(
                    'API Error (non-standard):',
                    response.status,
                    'URL:', response.url,
                    'Response:', json
                );
            }

            // Throw custom error with structured API error attached
            throw new ApiError(
                apiErrorResponse?.error?.message || USE_STATUS_BASED_MESSAGE,
                apiErrorResponse,
                response.status,
            );
        }
        return json;
    } catch (error) {
        // JSON parsing failed - likely HTML error page or non-JSON response
        if (!response.ok) {
            safeConsoleError(
                'API Error (non-JSON response):',
                response.status,
                'Content-Type:', contentType,
            );
            throw new ApiError(
                USE_STATUS_BASED_MESSAGE,
                null,
                response.status,
            );
        }
        // If response was OK but JSON parsing failed, re-throw the parsing error
        throw error;
    }
}

// SSE Configuration
export const SSE_CONFIG = {
    MAX_RETRIES: 10,
    RETRY_DELAY_MS: 2000,
    STREAM_TIMEOUT_MS: 55000, // Reconnect before pod timeout (60s)
};

/**
 * Validates and constructs the SSE stream URL for different stream types
 * @param streamId - UUID identifying the SSE stream
 * @param streamType - Type of stream:
 *   - 'roster': Roster copy operations (/sse/stream/{id})
 *   - 'processing_detail': Per-file detailed events (/files/processing-details/{id})
 * @internal Exported for testing purposes only
 */
export function buildSSEStreamUrl(streamId: string, streamType: 'roster' | 'processing_detail' = 'roster'): string {
    const host = determineAPIHost('/v2/network-optimization');
    const validatedHost = validateHost(host);

    // Build path based on stream type
    let basePath: string;
    switch (streamType) {
    case 'roster':
        basePath = '/v2/network-optimization/sse/stream';
        break;
    case 'processing_detail':
        basePath = '/v2/network-optimization/files/processing-details';
        break;
    default:
        basePath = '/v2/network-optimization/sse/stream';
    }

    const validatedPath = validatePath(`${basePath}/${streamId}`);
    return validatedHost + validatedPath;
}

/**
 * Build initiate endpoint URL for processing details SSE (per-file)
 * @param fileUploadId - File upload ID
 * @returns Validated URL for POST /files/processing-details/initiate/{id}
 * @internal Exported for testing purposes only
 */
export function buildProcessingDetailsInitiateUrl(fileUploadId: number): string {
    const host = determineAPIHost('/v2/network-optimization');
    const validatedHost = validateHost(host);
    const validatedPath = validatePath(`/v2/network-optimization/files/processing-details/initiate/${fileUploadId}`);
    return validatedHost + validatedPath;
}

/**
 * Reads from stream with timeout protection
 * @internal Exported for testing purposes only
 */
export async function readStreamWithTimeout(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    timeoutMs: number,
): Promise<ReadableStreamReadResult<Uint8Array>> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const timeoutPromise = new Promise<ReadableStreamReadResult<Uint8Array>>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('Stream read timeout')), timeoutMs);
    });

    try {
        const result = await Promise.race([reader.read(), timeoutPromise]);
        if (timeoutId) clearTimeout(timeoutId);
        return result;
    } catch (error: any) {
        if (timeoutId) clearTimeout(timeoutId);

        if (error.message === 'Stream read timeout') {
            // Clean up reader before throwing
            try {
                reader.cancel();
            } catch (e) {
                // Ignore cancel errors
            }
        }
        throw error;
    }
}

/**
 * Processes SSE lines and invokes appropriate callbacks
 */
export type SSELineResult = {
    isCompleteEvent: boolean;
    shouldStop: boolean;
};

/**
 * @internal Exported for testing purposes only
 */
export function processSSELine(
    line: string,
    isCompleteEvent: boolean,
    onMessage: (message: string) => void,
    onComplete: (status: 'COMPLETED' | 'FAILED') => void,
): SSELineResult {
    if (line.startsWith('event: complete')) {
        return {isCompleteEvent: true, shouldStop: false};
    }

    if (line.startsWith('data:')) {
        const data = line.startsWith('data: ') ? line.substring(6) : line.substring(5).trim();

        if (isCompleteEvent) {
            onComplete(data as 'COMPLETED' | 'FAILED');
            return {isCompleteEvent: false, shouldStop: true};
        }

        onMessage(data);
    }

    return {isCompleteEvent, shouldStop: false};
}

/**
 * Parses and processes SSE messages from buffer
 * @internal Exported for testing purposes only
 */
export function parseSSEMessages(
    buffer: string,
    isCompleteEvent: boolean,
    onMessage: (message: string) => void,
    onComplete: (status: 'COMPLETED' | 'FAILED') => void,
): {remainingBuffer: string; isCompleteEvent: boolean; shouldStop: boolean} {
    const lines = buffer.split('\n');
    const remainingBuffer = lines.pop() || '';
    let currentCompleteEvent = isCompleteEvent;
    let shouldStop = false;

    for (const line of lines) {
        const result = processSSELine(line, currentCompleteEvent, onMessage, onComplete);
        currentCompleteEvent = result.isCompleteEvent;

        if (result.shouldStop) {
            shouldStop = true;
            break;
        }
    }

    return {remainingBuffer, isCompleteEvent: currentCompleteEvent, shouldStop};
}

/**
 * Handles a single SSE connection attempt
 * @internal Exported for testing purposes only
 */
export async function handleSSEConnectionAttempt(
    url: string,
    streamTimeoutMs: number,
    connectionState: {
        isClosed: boolean;
        hasReceivedFinalStatus: boolean;
        abortController: AbortController | null;
    },
    onMessage: (message: string) => void,
    onComplete: (status: 'COMPLETED' | 'FAILED') => void,
): Promise<void> {
    // Create new AbortController for this connection attempt
    const abortController = new AbortController();
    connectionState.abortController = abortController;

    const authHeaders = await authHeader(true);

    // Security: CWE-201 - SSE connection for real-time progress updates
    // URL is pre-validated by buildSSEStreamUrl() with host/path validation
    // Authentication via JWT + Entra ID tokens, HTTPS encryption enforced
    const response = await fetch(url, {
        method: 'GET',
        headers: authHeaders,
        signal: abortController.signal,
    });

    if (!response.ok) {
        if (response.status === 401) {
            auth.logOut();
            throw new auth.InvalidAuthError();
        }
        throw new Error(`SSE connection failed: ${response.status}`);
    }

    if (!response.body) {
        throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let isCompleteEvent = false;

    try {
        while (!connectionState.isClosed && !connectionState.hasReceivedFinalStatus) {
            const readResult = await readStreamWithTimeout(reader, streamTimeoutMs);

            if (readResult.done) {
                break;
            }

            buffer += decoder.decode(readResult.value, {stream: true});
            const parseResult = parseSSEMessages(buffer, isCompleteEvent, onMessage, onComplete);

            buffer = parseResult.remainingBuffer;
            isCompleteEvent = parseResult.isCompleteEvent;

            if (parseResult.shouldStop) {
                connectionState.hasReceivedFinalStatus = true;
                connectionState.isClosed = true;
                break;
            }
        }
    } catch (error: any) {
        if (error.message === 'Stream read timeout') {
            abortController.abort();
        }

        if (error.name !== 'AbortError') {
            safeConsoleError('[SSE] Stream read error:', error);
        }

        throw error;
    } finally {
        try {
            await reader.cancel();
        } catch (cancelError) {
            // Ignore errors when cancelling (reader might already be closed)
        }

        connectionState.abortController = null;
    }
}

/**
 * Manages retry logic for SSE connections
 * @internal Exported for testing purposes only
 */
export async function retrySSEConnection(
    url: string,
    maxRetries: number,
    retryDelayMs: number,
    streamTimeoutMs: number,
    connectionState: {
        isClosed: boolean;
        hasReceivedFinalStatus: boolean;
        abortController: AbortController | null;
    },
    onMessage: (message: string) => void,
    onComplete: (status: 'COMPLETED' | 'FAILED') => void,
    onError: (error: Event | Error) => void,
): Promise<void> {
    let retryCount = 0;

    while (
        !connectionState.isClosed &&
        !connectionState.hasReceivedFinalStatus &&
        retryCount < maxRetries
    ) {
        if (connectionState.abortController) {
            connectionState.abortController.abort();
            connectionState.abortController = null;
        }

        try {
            await handleSSEConnectionAttempt(
                url,
                streamTimeoutMs,
                connectionState,
                onMessage,
                onComplete,
            );

            retryCount = 0; // Reset on successful connection

            if (!connectionState.hasReceivedFinalStatus && !connectionState.isClosed) {
                retryCount++;
            }
        } catch (error: any) {
            if (connectionState.isClosed) {
                break;
            }

            if (error.name === 'AbortError') {
                break;
            }

            if (connectionState.hasReceivedFinalStatus) {
                onError(error);
                break;
            }

            retryCount++;
            if (retryCount >= maxRetries) {
                onError(error);
                break;
            }
        }

        // Wait before reconnecting (if still retrying)
        if (
            !connectionState.hasReceivedFinalStatus &&
            !connectionState.isClosed &&
            retryCount > 0 &&
            retryCount < maxRetries
        ) {
            await new Promise((resolve) => setTimeout(resolve, retryDelayMs));

            // CRITICAL: Check again after wait - connection might have been closed during the delay
            if (connectionState.isClosed) {
                break;
            }
        }
    }

    // If we exhausted retries without getting final status
    if (
        !connectionState.hasReceivedFinalStatus &&
        retryCount >= maxRetries &&
        !connectionState.isClosed
    ) {
        onError(new Error('Maximum reconnection attempts exceeded'));
    }
}

/**
 * Creates an SSE connection with automatic reconnection
 * Used for roster copy operations and file processing streams
 *
 * @param streamId - UUID from /initiate endpoint
 * @param onMessage - Callback for progress events
 * @param onComplete - Callback for terminal state (COMPLETED/FAILED)
 * @param onError - Callback for connection errors
 * @param streamType - Type of stream ('roster' for roster operations, 'processing_detail' for file processing)
 */
export async function createSSEConnection(
    streamId: string,
    onMessage: (message: string) => void,
    onComplete: (status: 'COMPLETED' | 'FAILED') => void,
    onError: (error: Event | Error) => void,
    streamType: 'roster' | 'processing_detail' = 'roster',
): Promise<{close: () => void}> {
    const url = buildSSEStreamUrl(streamId, streamType);
    const connectionState = {
        isClosed: false,
        hasReceivedFinalStatus: false,
        abortController: null as AbortController | null,
    };

    // Start processing in background with automatic retry
    retrySSEConnection(
        url,
        SSE_CONFIG.MAX_RETRIES,
        SSE_CONFIG.RETRY_DELAY_MS,
        SSE_CONFIG.STREAM_TIMEOUT_MS,
        connectionState,
        onMessage,
        onComplete,
        onError,
    );

    return {
        close: () => {
            connectionState.isClosed = true;
            // Abort any active fetch request
            if (connectionState.abortController) {
                connectionState.abortController.abort();
                connectionState.abortController = null;
            }
        },
    };
}

// Session storage key for persisting stream IDs
const STREAM_STORAGE_KEY = 'network_optimization_streams';

// Helper functions for managing stream persistence
export const streamPersistence = {
    // Save a stream ID with metadata
    saveStream: (streamId: string, metadata: Record<string, any>) => {
        try {
            const streams = streamPersistence.getAllStreams();
            streams[streamId] = {...metadata, timestamp: Date.now()};
            sessionStorage.setItem(STREAM_STORAGE_KEY, JSON.stringify(streams));
        } catch (error) {
            safeConsoleError('Failed to save stream to sessionStorage:', error);
        }
    },

    // Get a specific stream
    getStream: (streamId: string) => {
        const streams = streamPersistence.getAllStreams();
        return streams[streamId] || null;
    },

    // Get all active streams
    getAllStreams: (): Record<string, any> => {
        try {
            const stored = sessionStorage.getItem(STREAM_STORAGE_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch (error) {
            safeConsoleError('Failed to read streams from sessionStorage:', error);
            return {};
        }
    },

    // Remove a stream when it completes
    removeStream: (streamId: string) => {
        try {
            const streams = streamPersistence.getAllStreams();
            delete streams[streamId];
            sessionStorage.setItem(STREAM_STORAGE_KEY, JSON.stringify(streams));
        } catch (error) {
            safeConsoleError('Failed to remove stream from sessionStorage:', error);
        }
    },

    // Clear all streams
    clearAllStreams: () => {
        try {
            sessionStorage.removeItem(STREAM_STORAGE_KEY);
        } catch (error) {
            safeConsoleError('Failed to clear streams from sessionStorage:', error);
        }
    },

    // Clean up old streams (older than 2 hours by default)
    cleanupOldStreams: (maxAgeMs = 2 * 60 * 60 * 1000) => {
        const streams = streamPersistence.getAllStreams();
        const cutoffTime = Date.now() - maxAgeMs;
        let hasChanges = false;

        Object.keys(streams).forEach((streamId) => {
            if (streams[streamId].timestamp < cutoffTime) {
                delete streams[streamId];
                hasChanges = true;
            }
        });

        if (hasChanges) {
            sessionStorage.setItem(STREAM_STORAGE_KEY, JSON.stringify(streams));
        }
    },
};

export const fileProcessingStreamPersistence = {
    // Save file processing stream mapping
    // Ensures only one stream per file_upload_id by removing any existing streams for the same file
    saveStream: (streamId: string, fileUploadId: number, fileName: string) => {
        try {
            const streams = fileProcessingStreamPersistence.getAllStreams();

            // Remove any existing stream for this file_upload_id to ensure only one stream per file
            for (const [existingStreamId, data] of Object.entries(streams)) {
                if (data.file_upload_id === fileUploadId && existingStreamId !== streamId) {
                    delete streams[existingStreamId];
                    safeConsoleLog(`Removed duplicate stream ${existingStreamId} for file_upload_id ${fileUploadId}`);
                }
            }

            streams[streamId] = {
                file_upload_id: fileUploadId,
                file_name: fileName,
                timestamp: Date.now()
            };
            sessionStorage.setItem(FILE_PROCESSING_STREAMS_KEY, JSON.stringify(streams));
        } catch (error) {
            safeConsoleError('Failed to save file processing stream:', error);
        }
    },

    // Get stream by stream_id
    getStream: (streamId: string): FileProcessingStreamEntry | null => {
        const streams = fileProcessingStreamPersistence.getAllStreams();
        return streams[streamId] || null;
    },

    // Find stream by file_upload_id
    findByFileId: (fileUploadId: number): {streamId: string; data: FileProcessingStreamEntry} | null => {
        const streams = fileProcessingStreamPersistence.getAllStreams();
        for (const [streamId, data] of Object.entries(streams)) {
            if (data.file_upload_id === fileUploadId) {
                return {streamId, data};
            }
        }
        return null;
    },

    // Get all active file processing streams
    getAllStreams: (): Record<string, FileProcessingStreamEntry> => {
        try {
            const stored = sessionStorage.getItem(FILE_PROCESSING_STREAMS_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch (error) {
            safeConsoleError('Failed to read file processing streams:', error);
            return {};
        }
    },

    // Remove stream when complete
    removeStream: (streamId: string) => {
        try {
            const streams = fileProcessingStreamPersistence.getAllStreams();
            delete streams[streamId];
            sessionStorage.setItem(FILE_PROCESSING_STREAMS_KEY, JSON.stringify(streams));
        } catch (error) {
            safeConsoleError('Failed to remove file processing stream:', error);
        }
    },

    // Clean up old streams (default: 2 hours)
    cleanupOldStreams: (maxAgeMs = 2 * 60 * 60 * 1000) => {
        const streams = fileProcessingStreamPersistence.getAllStreams();
        const cutoffTime = Date.now() - maxAgeMs;
        let hasChanges = false;

        Object.keys(streams).forEach((streamId) => {
            if (streams[streamId].timestamp < cutoffTime) {
                delete streams[streamId];
                hasChanges = true;
            }
        });

        if (hasChanges) {
            sessionStorage.setItem(FILE_PROCESSING_STREAMS_KEY, JSON.stringify(streams));
        }

        return hasChanges;
    },

    // Clear all file processing streams
    clearAll: () => {
        try {
            sessionStorage.removeItem(FILE_PROCESSING_STREAMS_KEY);
        } catch (error) {
            safeConsoleError('Failed to clear file processing streams:', error);
        }
    }
};

async function putBinaryData(path: string, data: ArrayBuffer, signal?: AbortSignal, forceEntraAuth = false) {
    const authHeaders = await authHeader(forceEntraAuth);
    const headers = {...authHeaders, 'Content-Type': 'application/octet-stream'};
    const host = determineAPIHost(path);

    // Security: CWE-201 - Binary data transmission is required for file upload functionality
    // Security controls:
    // 1. Host whitelist validation (validateHost) - only approved API servers
    // 2. Path validation (validatePath) - prevents SSRF attacks
    // 3. HTTPS encryption for all remote hosts (see protocol constant)
    // 4. Authentication via JWT + optional Entra ID tokens (authHeaders)
    // 5. Same-origin policy enforcement for local development (127.0.0.1:8080)
    const validatedHost = validateHost(host);
    const validatedPath = validatePath(path);

    const response = await fetch(validatedHost + validatedPath, {method: 'PUT', body: data, headers, signal});
    return await handleResponse(response);
}

async function post(
    path: string,
    payload: object,
    signal?: AbortSignal,
    customHeaders?: Array<{[key: string]: string}>,
    apiBase?: string,
    forceEntraAuth = false,
) {
    return makeRequest('POST', path, signal, payload, forceEntraAuth, customHeaders, apiBase);
}

async function put(path: string, payload: object, signal?: AbortSignal, forceEntraAuth = false) {
    return makeRequest('PUT', path, signal, payload, forceEntraAuth);
}

async function patch(path: string, payload: object, signal?: AbortSignal, forceEntraAuth = false) {
    return makeRequest('PATCH', path, signal, payload, forceEntraAuth);
}

async function del(path: string, signal?: AbortSignal, forceEntraAuth = false, payload?: object) {
    return makeRequest('DELETE', path, signal, payload, forceEntraAuth);
}

async function get(
    path: string,
    signal?: AbortSignal,
    customHeaders?: Array<{[key: string]: string}>,
    forceEntraAuth = false,
) {
    return makeRequest('GET', path, signal, undefined, forceEntraAuth, customHeaders);
}

const ffEnv = {
    ffEnvKey: 'useFF',
    setFFEnv: function () {
        if (auth.token) {
            sessionStorage.clear();
            sessionStorage.setItem(ffEnv.ffEnvKey, 'true');
        }
    },
    clearFFEnv: function () {
        sessionStorage.clear();
    },
    useFFEnv: function () {
        return Boolean(sessionStorage.getItem(ffEnv.ffEnvKey));
    },
    toggleEnv: function (): boolean {
        if (ffEnv.useFFEnv()) ffEnv.clearFFEnv();
        else ffEnv.setFFEnv();

        return ffEnv.useFFEnv();
    },
};

const analytics = {
    save: async (eventName: string, payload: object) =>
        post('/v2/a/in', {name: eventName, ...payload}),
    org: {
        checkAccess: async (orgId: number, signal: AbortSignal) =>
            get(`/v2/analytics/org/${orgId}/checkAccess`, signal),
    },
};

const savedMarkets = {
    listByUser: async (signal: AbortSignal) => get('/v2/vis/markets?for=user', signal),
    listByUserOrgs: async (signal: AbortSignal) => get('/v2/vis/markets?for=userOrgs', signal),
};

const quantum = {
    execute: async (obj: object, signal: AbortSignal, streamResponse?: boolean) =>
        post(
            '/v2/quantum/data/execute',
            obj,
            signal,
            streamResponse ? [{'Stream-Type': 'chunked'}] : undefined,
        ),
    metadata: async (obj: object, signal: AbortSignal) => post('/v2/quantum/metadata', obj, signal),
    datasetMetadata: async (dataset: string, signal: AbortSignal) =>
        get('/v2/quantum/metadata/' + dataset, signal),
    datasetInfo: async (dataset: string, signal: AbortSignal) =>
        get(`/v2/quantum/metadata/${dataset}/info`, signal),
    batchDatasetMetadata: async (datasets: Array<string>, signal: AbortSignal) =>
        post('/v2/quantum/metadata/batch', {datasets}, signal),
    batchDatasetInfo: async (datasets: Array<string>, signal: AbortSignal) =>
        post('/v2/quantum/metadata/batch/info', {datasets}, signal),
    wakeUpDatabase: async () => get('/v2/quantum/wake-up-database'),
};

const accessRoles = {
    list: async (signal: AbortSignal) => get('/v2/user/access-roles', signal),
    create: async (accessRole: any) => post('/v2/user/access-roles', accessRole),
    update: async (accessRole: any) =>
        put(`/v2/user/access-roles/${accessRole.id}`, {
            name: accessRole.name,
            description: accessRole.description,
        }),
    delete: async (id: number) => del(`/v2/user/access-roles/${id}`),
};

const modules = {
    list: async (signal: AbortSignal) => get('/v2/user/modules', signal),
    create: async (module: any) => post('/v2/user/modules', module),
    update: async (module: any) => put(`/v2/user/modules/${module.id}`, module),
    delete: async (id: number) => del(`/v2/user/modules/${id}`),
    datasets: {
        list: async (signal: AbortSignal) => get('/v2/user/modules/datasets', signal),
        create: async (datasetModule: any) => post('/v2/user/modules/datasets', datasetModule),
        get: async (dataset: string) => get(`/v2/user/modules/datasets/${dataset}`),
        update: async (datasetModule: any) =>
            put(`/v2/user/modules/datasets/${datasetModule.id}`, datasetModule),
        delete: async (id: number) => del(`/v2/user/modules/datasets/${id}`),
    },
    canvas: {
        list: async (signal: AbortSignal) => get('/v2/user/modules/canvas', signal),
        create: async (canvasModule: any) => post('/v2/user/modules/canvas', canvasModule),
        update: async (canvasModule: any) =>
            put(`/v2/user/modules/canvas/${canvasModule.id}`, canvasModule),
        delete: async (id: number) => del(`/v2/user/modules/canvas/${id}`),
    },
};

const users = {
    list: async (signal: AbortSignal) => get('/v2/user/users', signal),
    create: async (user: any) => post('/v2/user/users', user),
    modify: async (user: any) => patch(`/v2/user/users/${user.id}`, user),
    update: async (user: any) => put(`/v2/user/users/${user.id}`, user),
    delete: async (id: number) => del(`/v2/user/users/${id}`),
    datasets: {
        list: (signal: AbortSignal) => get('/v2/user/user-datasets', signal),
    },
    proxy: {
        proxy: async (id: number) => post(`/v2/user/authenticate/proxy/${id}`, {}),
    },
    passwordReset: {
        reset: (email: string) => post(`/v2/user/password-reset`, {email}),
        check: (email: string, code: string) =>
            post(`/v2/user/password-reset/check`, {email, code}),
        change: (email: string, code: string, password: string) =>
            post(`/v2/user/password-reset/change`, {email, code, password}),
    },
    join: {
        checkCode: (code: string) => get(`/v2/user/join/${code}`),
        setPassword: (code: string, password: string) => post(`/v2/user/join/${code}`, {password}),
    },
};

const organizations = {
    list: async (signal: AbortSignal) => get('/v2/user/organizations?all=true', signal),
    listByUser: async (signal: AbortSignal) => get('/v2/user/organizations', signal),
    get: async (id: number, signal: AbortSignal) => get(`/v2/user/organizations/${id}`, signal),
    create: async (org: any) => post('/v2/user/organizations', org),
    update: async (org: any) => put(`/v2/user/organizations/${org.id}`, org),
    modify: async (org: any) => patch(`/v2/user/organizations/${org.id}`, org),
    delete: async (orgId: number) => del(`/v2/user/organizations/${orgId}`),
    users: {
        add: async (orgId: number, userId: number) =>
            patch(`/v2/user/organizations/${orgId}/users/${userId}`, {}),
        get: async (orgId: number, userId: number, signal: AbortSignal) =>
            get(`/v2/user/organizations/${orgId}/users/${userId}`, signal),
        list: async (orgId: number, signal: AbortSignal) =>
            get(`/v2/user/organizations/${orgId}/users`, signal),
        listInvitees: async (orgId: number, signal: AbortSignal) =>
            get(`/v2/user/organizations/${orgId}/invitees`, signal),
        invite: async (orgId: number, invitation: object) =>
            patch(`/v2/user/organizations/${orgId}/users`, invitation),
        create: async (orgId: number, userId: number | string) =>
            patch(`/v2/user/organizations/${orgId}/users/${userId}`, {}),
        delete: async (orgId: number, userId: number) =>
            del(`/v2/user/organizations/${orgId}/users/${userId}`),
        update: async (membership: Membership) =>
            put(
                `/v2/user/organizations/${membership.orgId}/users/${membership.userId}`,
                membership,
            ),
        passwordReset: async (orgId: number, userId: number) =>
            post(`/v2/user/organizations/${orgId}/users/${userId}/password-reset`, {}),
    },
    domains: {
        list: async (orgId: number, signal: AbortSignal) =>
            get(`/v2/user/organizations/${orgId}/domains`, signal),
        create: async (orgId: number, domain: dataValue) =>
            post(`/v2/user/organizations/${orgId}/domains`, {domain}),
        delete: async (orgId: number, domainId: number) =>
            del(`/v2/user/organizations/${orgId}/domains/${domainId}`),
    },
    proxy: async (id: number) => post(`/v2/user/authenticate/refresh?orgId=${id}`, {}),
};

const dashboards = {
    list: async (signal: AbortSignal) => get('/v2/vis/dashboards', signal),
    listByUser: async (signal: AbortSignal) => get('/v2/vis/dashboards?for=user', signal),
    listByUserOrgs: async (signal: AbortSignal) => get('/v2/vis/dashboards?for=userOrgs', signal),
    create: async (dashboard: any) => post('/v2/vis/dashboards', dashboard),
    update: async (dashboard: any) => put(`/v2/vis/dashboards/${dashboard.id}`, dashboard),
    modify: async (dashboard: any) => patch(`/v2/vis/dashboards/${dashboard.id}`, dashboard),
    delete: async (id: number) => del(`/v2/vis/dashboards/${id}`),
    tags: {
        list: async () => [
            {id: 1, name: 'Geography'},
            {id: 2, name: 'Topics'},
            {id: 3, name: 'Entities and Organizations'},
            {id: 4, name: 'Other'},
        ],
    },
};

const mapConfigs = {
    list: async (signal: AbortSignal) => get('/v2/vis/map-config', signal),
    listByUser: async (signal: AbortSignal) => get('/v2/vis/map-config?for=user', signal),
    listByUserOrgs: async (signal: AbortSignal) => get('/v2/vis/map-config?for=userOrgs', signal),
    create: async (mapConfig: any) => post('/v2/vis/map-config', mapConfig),
    update: async (mapConfig: any) => put(`/v2/vis/map-config/${mapConfig.id}`, mapConfig),
    delete: async (id: number) => del(`/v2/vis/map-config/${id}`),
};

const visualizations = {
    listByUser: async (signal: AbortSignal) => get('/v2/vis/visualizations?for=user', signal),
    listByUserOrgs: async (signal: AbortSignal) =>
        get('/v2/vis/visualizations?for=userOrgs', signal),
};

const userDatasets = {
    list: async (signal: AbortSignal) => get('/v2/user/user-datasets', signal),
    create: async (dataset: any) => post('/v2/user/user-datasets', dataset),
    modify: async (dataset: any) => patch(`/v2/user/user-datasets/${dataset.id}`, dataset),
    update: async (dataset: any) => put(`/v2/user/user-datasets/${dataset.id}`, dataset),
    delete: async (id: number | string) => del(`/v2/user/user-datasets${id}`),
};

const datasetPermissions = {
    validate: async (dataset: string, signal?: AbortSignal) =>
        post('/v2/user/validate/dataset', {id: dataset, accessType: 1}, signal),
    validateAll: async (datasets: string[], signal?: AbortSignal) =>
        post('/v2/user/validate-all/dataset', {ids: datasets, accessType: 1}, signal),
};

const savedQuery = {
    list: {
        all: async (signal: AbortSignal) => get('/v2/cogniboard/saved-query', signal),
        forUser: async (signal: AbortSignal) => get('/v2/cogniboard/saved-query?for=user', signal),
    },
    get: async (queryId: number, signal: AbortSignal) =>
        get(`/v2/cogniboard/saved-query/${queryId}`, signal),
    create: async (savedQuery: SavedQuery) => post('/v2/cogniboard/saved-query', savedQuery),
    modify: async (savedQuery: SavedQuery) =>
        patch(`/v2/cogniboard/saved-query/${savedQuery.id}`, savedQuery),
    delete: async (id: number) => del(`/v2/cogniboard/saved-query/${id}`),
};

const hcIndex = {
    sections: async (signal: AbortSignal) => get('/v2/cogniboard/healthcare-index/section', signal),
    getById: async (id: number, signal: AbortSignal) =>
        get(`/v2/cogniboard/healthcare-index/entry/${id}`, signal),
    getByShortName: async (shortName: string, signal: AbortSignal) =>
        get(`/v2/cogniboard/healthcare-index/entry/short-name/${shortName}`, signal),
    getBlindProd: async (signal: AbortSignal) =>
        get('/v2/cogniboard/healthcare-index/blind-production/entries', signal),
};

const insightContainer = {
    list: {
        all: async (signal: AbortSignal) => get('/v2/cogniboard/insight-container', signal),
        forUser: async (signal: AbortSignal) =>
            get('/v2/cogniboard/insight-container?for=user', signal),
    },
    get: async (id: number, signal: AbortSignal) =>
        get(`/v2/cogniboard/insight-container/${id}`, signal),
    create: async (insightContainer: any) =>
        post('/v2/cogniboard/insight-container', insightContainer),
    delete: async (id: number) => del(`/v2/cogniboard/insight-container/${id}`),
};

const canvas = {
    list: async (signal: AbortSignal) => get('/v2/cogniboard/canvas', signal),
    listByUser: async (signal: AbortSignal) => get('/v2/cogniboard/canvas?for=user', signal),
    get: async (id: number, signal?: AbortSignal) => get(`/v2/cogniboard/canvas/${id}`, signal),
    getDraft: async (id: number, signal?: AbortSignal) =>
        get(`/v2/cogniboard/canvas/draft/${id}`, signal),
    getByTitle: async (title: string, signal?: AbortSignal) =>
        get(`/v2/cogniboard/canvas/title/${title}`, signal),
    getByTitleDraft: async (title: string, signal?: AbortSignal) =>
        get(`/v2/cogniboard/canvas/title/draft/${title}`, signal),
    getCanvasVersions: async (id: number, signal?: AbortSignal) =>
        get(`/v2/cogniboard/canvas/versions/${id}`, signal),
    getCanvasVersion: async (id: number, signal?: AbortSignal) =>
        get(`/v2/cogniboard/canvas/version/${id}`, signal),
    create: async (canvas: any) => post('/v2/cogniboard/canvas', canvas),
    update: async (canvas: any) => put(`/v2/cogniboard/canvas/${canvas.id}`, canvas),
    publish: async (canvas: any) => put(`/v2/cogniboard/canvas/publish/${canvas.id}`, canvas),
    delete: async (id: number) => del(`/v2/cogniboard/canvas/${id}`),
    listSections: async (signal: AbortSignal) => get('/v2/cogniboard/canvas/section', signal),
    getLink: async (dataset: string, signal: AbortSignal) =>
        get(`/v2/cogniboard/canvas/link/${dataset}`, signal),
};

const download = {
    list: async (signal: AbortSignal) => get('/v2/data/downloads', signal),
    trigger: async () => post('/v2/downloads/trigger', {}),
    user: {
        request: async (query: any) => post('/v2/query/ext-query/async-download', query),
        list: async (signal: AbortSignal, all = false) =>
            get(`/v2/downloads/user-downloads${all ? '?all=true' : ''}`, signal),
    },
};

const search: Record<string, any> = {
    metadata: async (query: string, signal: AbortSignal) =>
        get(`/v2/search/metadata?q=${encodeURIComponent(query)}`, signal),
    entities: async (query: string, state: string, city: string, signal: AbortSignal) =>
        get(
            `/v2/search/entity?q=${encodeURIComponent(query)}` +
                `${state && '&state=' + encodeURIComponent(state)}` +
                `${city && '&city=' + encodeURIComponent(city)}`,
            signal,
        ),
    entitiesByType: async (
        query: string,
        type: string,
        state: string,
        city: string,
        signal: AbortSignal,
    ) =>
        get(
            `/v2/search/entity?q=${encodeURIComponent(query)}` +
                `${type && '&entityType=' + encodeURIComponent(type)}` +
                `${state && '&state=' + encodeURIComponent(state)}` +
                `${city && '&city=' + encodeURIComponent(city)}`,
            signal,
        ),
    regions: async (query: string, signal: AbortSignal) =>
        get(`/v2/search/region?q=${encodeURIComponent(query)}`, signal),
    tools: async (query: string, signal: AbortSignal) =>
        get(`/v2/search/tool?q=${encodeURIComponent(query)}`, signal),
    dashboards: async (query: string, signal: AbortSignal) =>
        get(`/v2/search/dashboard?q=${encodeURIComponent(query)}`, signal),
    states: async (query: string, signal: AbortSignal) =>
        get(`/v2/search/states?q=${encodeURIComponent(query)}`, signal),
    cities: async (query: string, signal: AbortSignal) =>
        get(`/v2/search/cities?q=${encodeURIComponent(query)}`, signal),
};

const siteSearch = {
    searchAll: async (query: string, signal: AbortSignal, numRows?: number) =>
        get(
            `/v2/site-search/search?q=${encodeURIComponent(query)}${
                numRows ? `&numRows=${numRows}` : ''
            }`,
            signal,
        ),
    searchHCI: async (query: string, signal: AbortSignal) =>
        get(`/v2/site-search/search/hci?q=${encodeURIComponent(query)}`, signal),
    searchTool: async (query: string, signal: AbortSignal) =>
        get(`/v2/site-search/search/tools?q=${encodeURIComponent(query)}`, signal),
    listCanvasIndexes: async (signal: AbortSignal) => get('/v2/site-search/', signal),
    searchCanvas: async (searchCanvas: SearchCanvas, signal: AbortSignal) =>
        post(`/v2/site-search/search/canvas`, searchCanvas, signal),
};

const siteConfig = {
    get: async (name: string, signal: AbortSignal) => get(`/v2/vis/config/${name}`, signal),
};

const contentAccess = {
    list: async (signal: AbortSignal) => get('/v2/user/modules/content-access', signal),
    get: async (id: number, sourceType: string, signal: AbortSignal) =>
        get(`/v2/modules/content-access/${sourceType}/${id}`, signal),
    create: async (contentAccess: any) => post('/v2/user/modules/content-access', contentAccess),
    update: async (contentAccess: any) =>
        put(`/v2/user/modules/content-access/${contentAccess.id}`, contentAccess),
    delete: async (contentAccessId: number) =>
        del(`/v2/user/modules/content-access/${contentAccessId}`),
};

const geography = {
    list: async (shape: string, signal: AbortSignal) => get(`/v2/geography/${shape}`, signal),
    listAll: async (signal: AbortSignal) => get('/v2/geography/', signal),
    get: async (shape: string, year: string, size: string, signal: AbortSignal) =>
        get(`/v2/geography/${shape}/${year}/${size}`, signal),
    getPartial: async (
        shape: string,
        year: string,
        size: string,
        geoIds: Array<string>,
        signal: AbortSignal,
    ) => post(`/v2/geography/${shape}/${year}/${size}`, {geo_ids: geoIds}, signal),
    getWithinDistance: async (
        shape: string,
        year: string,
        size: string,
        body: GeographyDistance,
        signal: AbortSignal,
    ) => post(`/v2/geography/${shape}/${year}/${size}/distance`, body, signal),
};

const content = {
    myFavorites: {
        listAll: async (signal: AbortSignal) => get('/v2/content/favorites', signal),
        listTop: async (signal: AbortSignal) => get('/v2/content/favorites/top', signal),
        create: async (myFavorite: MyFavorite) => post('/v2/content/favorites', myFavorite),
        delete: async (myFavoriteId: number) => del(`/v2/content/favorites/${myFavoriteId}`),
    },
    spotlight: {
        list: async (signal: AbortSignal) => get('/v2/content/spotlight', signal),
        listN: async (number: number, signal: AbortSignal) =>
            get(`/v2/content/spotlight/date/${number}`, signal),
    },
};

//network optimization
const networkOptimization = {
    getStreamStatus: async (streamId: string, signal?: AbortSignal) =>
        get(`/v2/network-optimization/sse/stream-status/${streamId}`, signal, undefined, true),
    fetchPatientGeoJson: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        distanceCalcType: string,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/adequacy/patient/${runId}/${revisionId}`,
            {...filters, distance_calc_type: distanceCalcType},
            signal,
            [{'Content-Type': 'application/geo-json'}],
            undefined,
            true,
        ),
    fetchPatientPopulationGeoJson: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        distanceCalcType: string,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/adequacy/patient/population/${runId}/${revisionId}`,
            {...filters, distance_calc_type: distanceCalcType},
            signal,
            [{'Content-Type': 'application/geo-json'}],
            undefined,
            true,
        ),
    fetchDAPatientGeoJson: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/adequacy/patient/disruption-analysis/${runId}/${revisionId}`,
            filters,
            signal,
            [{'Content-Type': 'application/geo-json'}],
            undefined,
            true,
        ),
    fetchDAPatientPopulationGeoJson: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/adequacy/patient/population/disruption-analysis/${runId}/${revisionId}`,
            filters,
            signal,
            [{'Content-Type': 'application/geo-json'}],
            undefined,
            true,
        ),
    fetchProviderGeoJson: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/adequacy/provider/${runId}/${revisionId}`,
            {...filters},
            signal,
            [{'Content-Type': 'application/geo-json'}],
            undefined,
            true,
        ),
    fetchProviderGeoJsonBatch: async (
        filters: Record<string, string | string[]>,
        runId: number,
        revisionId: number,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/adequacy/provider/batch/${runId}/${revisionId}`,
            {...filters},
            signal,
            [{'Content-Type': 'application/geo-json'}],
            undefined,
            true,
        ),
    fetchProviderReport: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/adequacy/provider/${runId}/${revisionId}`,
            {...filters},
            signal,
            undefined,
            undefined,
            true,
        ),
    fetchDAProviderGeoJson: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/adequacy/provider/disruption-analysis/${runId}/${revisionId}`,
            filters,
            signal,
            [{'Content-Type': 'application/geo-json'}],
            undefined,
            true,
        ),
    fetchDAProviderReport: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/adequacy/provider/disruption-analysis/${runId}/${revisionId}`,
            filters,
            signal,
            undefined,
            undefined,
            true,
        ),
    fetchDisruptionAnalysisComparison: async (
        filters: Record<string, string | Array<string>>,
        runId: number,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/compare/disruption-analysis/${runId}`,
            filters,
            signal,
            undefined,
            undefined,
            true,
        ),
    fetchNetworkAdequacyComparison: async (
        filters: Record<string, string | Array<string>>,
        runId: number,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/compare/network-adequacy/${runId}`,
            filters,
            signal,
            undefined,
            undefined,
            true,
        ),
    fetchProviderRoster: async (
        filters: IGeographyFilterPayload,
        runId: number,
        revisionId: number,
        page: number,
        pageLimit: number,
        viewType: string,
        specialtyFilterCode: string,
        signal: AbortSignal,
        orderBy?: SortOrderItem,
    ) =>
        post(
            `/v2/network-optimization/roster/provider/${runId}/${revisionId}`,
            {
                ...filters,
                page,
                limit: pageLimit,
                view_type: viewType,
                specialty_filter_code: specialtyFilterCode,
                ...(orderBy && {order_by: orderBy}),
            },
            signal,
            undefined,
            undefined,
            true,
        ),
    fetchGeographyFilters: async (runId: number, signal?: AbortSignal) =>
        get(
            `/v2/network-optimization/adequacy/geographies-table/${runId}`,
            signal,
            undefined,
            true,
        ),
    fetchDisruptiveAnalysisFilters: async (runId: number, signal?: AbortSignal) =>
        get(
            `/v2/network-optimization/adequacy/disruption-analysis-filter/${runId}`,
            signal,
            undefined,
            true,
        ),
    fetchProviders: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        distanceCalcType: string,
        signal: AbortSignal,
        page?: number,
    ) =>
        post(
            `/v2/network-optimization/providers/${runId}/${revisionId}`,
            {...filters, distance_calc_type: distanceCalcType, ...(page !== undefined && {page})},
            signal,
            undefined,
            undefined,
            true,
        ),
    fetchDAProviders: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        signal: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/providers/disruption-analysis/${runId}/${revisionId}`,
            filters,
            signal,
            undefined,
            undefined,
            true,
        ),
    fetchSpecialtyReport: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        distanceCalcType: string,
        signal?: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/adequacy/specialty/${runId}/${revisionId}`,
            {...filters, distance_calc_type: distanceCalcType},
            signal,
            undefined,
            undefined,
            true,
        ),
    fetchDisruptionAnalysisSpecialtyReport: async (
        filters: Record<string, string>,
        runId: number,
        revisionId: number,
        signal?: AbortSignal,
    ) =>
        post(
            `/v2/network-optimization/adequacy/specialty/disruption-analysis/${runId}/${revisionId}`,
            filters,
            signal,
            undefined,
            undefined,
            true,
        ),
    recalculateRevisionSSE: async (
        runId: number,
        revisionId: number,
        specialtyCode: string,
        county: string,
        standard_type: string,
        standard_version: string,
        patient_roster_id: string,
        signal: AbortSignal,
    ): Promise<{stream_id: string}> =>
        post(
            `/v2/network-optimization/adequacy/recalculate-sse/${runId}/${revisionId}/${specialtyCode}`,
            {county, standard_type, standard_version, patient_roster_id},
            signal,
            undefined,
            undefined,
            true,
        ),
    fetchNavItems: async (): Promise<NavItem[]> => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve([
                    {label: 'My Files', path: 'my-files', useRosterID: false, hideTab: false},
                    {
                        label: 'Network Analysis',
                        path: 'network-analysis',
                        useRosterID: true,
                        hideTab: false,
                    },
                    {label: 'Reports', path: 'reports', useRosterID: true, hideTab: false},
                    {
                        label: 'Compare',
                        path: 'compare',
                        useRosterID: true,
                        hideTab: !checkAccess(SiteFeature.NetworkOptimizationRosterComparison),
                    },
                ]);
            }, 1000);
        });
    },
    downloadProviderRoster: (
        runId = 1,
        revisionId: number,
        type: string,
        format: 'csv' | 'txt',
        signal?: AbortSignal,
    ): Promise<Response> =>
        streamGetRequest(
            `/v2/network-optimization/roster/provider/${type}/${runId}/${revisionId}/providers?format=${format}`,
            signal,
        ),
    downloadFacilityRoster: (
        runId = 1,
        revisionId: number,
        type: string,
        format: 'csv' | 'txt',
        signal?: AbortSignal,
    ): Promise<Response> =>
        streamGetRequest(
            `/v2/network-optimization/roster/provider/${type}/${runId}/${revisionId}/facilities?format=${format}`,
            signal,
        ),
    downloadCombinedRoster: (
        runId = 1,
        revisionId: number,
        type: string,
        format: 'csv' | 'txt',
        signal?: AbortSignal,
    ): Promise<Response> =>
        streamGetRequest(
            `/v2/network-optimization/roster/provider/${type}/${runId}/${revisionId}/combined?format=${format}`,
            signal,
        ),
    updateProviderRoster: async (
        runId: number,
        revisionId: number,
        specialtyCode: string,
        providersToAdd: Array<string>,
        providersToRemove: Array<string>,
        signal: AbortSignal,
    ) => {
        const requestBody: any = {};
        if (providersToAdd.length > 0) {
            requestBody.providers_to_add = providersToAdd;
        }

        if (providersToRemove.length > 0) {
            requestBody.providers_to_remove = providersToRemove;
        }
        return post(
            `/v2/network-optimization/roster/provider-roster/${runId}/${revisionId}/${specialtyCode}/update`,
            requestBody,
            signal,
            undefined,
            undefined,
            true,
        );
    },
    listProviderRosters: async (signal: AbortSignal) =>
        get('/v2/network-optimization/roster/user-provider-rosters', signal, undefined, true),
    listPatientRosters: async (signal: AbortSignal) =>
        get('/v2/network-optimization/roster/user-patient-rosters', signal, undefined, true),
    resyncProviderRosterRevision: async (
        runId: number,
        revisionId: number,
        specialtyCode: string,
        signal: AbortSignal,
    ) =>
        get(
            `/v2/network-optimization/roster/provider-roster/revision-sync/${runId}/${revisionId}/${specialtyCode}`,
            signal,
            undefined,
            true,
        ),
    copyRosterSSE: async (
        rosterId: number,
        revisionName: string,
        notes: string,
        signal: AbortSignal,
    ): Promise<{stream_id: string}> => {
        const requestBody = {
            revision_name: revisionName,
            revision_notes: notes,
        };
        return post(
            `/v2/network-optimization/roster/provider-roster/${rosterId}/copy-sse`,
            requestBody,
            signal,
            undefined,
            undefined,
            true,
        );
    },
    renameRoster: async (
        rosterId: number,
        revisionId: number,
        revisionName: string,
        notes: string | null,
        signal: AbortSignal,
    ) => {
        const requestBody = {
            revision_name: revisionName,
            revision_notes: notes,
        };
        return post(
            `/v2/network-optimization/roster/provider-roster/${rosterId}/${revisionId}/rename`,
            requestBody,
            signal,
            undefined,
            undefined,
            true,
        );
    },
    deleteRoster: async (rosterId: number, revisionId: number, signal: AbortSignal) => {
        return del(
            `/v2/network-optimization/roster/provider-roster/${rosterId}/${revisionId}/delete`,
            signal,
            true,
        );
    },
    deleteFile: async (fileId: string, filename: string, signal: AbortSignal) => {
        return del(
            `/v2/network-optimization/files/${fileId}`,
            signal,
            true,
            {filename},
        );
    },
    renameFile: async (fileId: string, filename: string, signal: AbortSignal): Promise<{message: string; filename: string}> => {
        return patch(
            `/v2/network-optimization/files/${fileId}`,
            {filename},
            signal,
            true,
        );
    },
    fetch_available_providers_report: async (payload: IFetchReportsPayload) => {
        const {roster_id, revision_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/providers/available-providers-report/${roster_id}/${revision_id}`,
            rest,
            signal,
            undefined,
            undefined,
            true,
        );
    },
    download_available_providers_report: async (payload: IFetchReportsPayload) => {
        const {roster_id, revision_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/providers/available-providers-report/${roster_id}/${revision_id}`,
            rest,
            signal,
            [{'Content-Type': 'text/csv'}],
            undefined,
            true,
        );
    },
    fetch_providers_report: async (payload: IFetchReportsPayload) => {
        const {roster_id, revision_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/providers/providers-report/${roster_id}/${revision_id}`,
            rest,
            signal,
            undefined,
            undefined,
            true,
        );
    },
    download_providers_report: async (payload: IFetchReportsPayload) => {
        const {roster_id, revision_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/providers/providers-report/${roster_id}/${revision_id}`,
            rest,
            signal,
            [{'Content-Type': 'text/csv'}],
            undefined,
            true,
        );
    },
    fetch_adequacy_scores: async (payload: IFetchReportsPayload) => {
        const {roster_id, revision_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/adequacy/adequacy-score/${roster_id}/${revision_id}`,
            rest,
            signal,
            undefined,
            undefined,
            true,
        );
    },
    download_adequacy_scores_report: async (payload: IFetchReportsPayload) => {
        const {roster_id, revision_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/adequacy/adequacy-score/${roster_id}/${revision_id}`,
            rest,
            signal,
            [{'Content-Type': 'text/csv'}],
            undefined,
            true,
        );
    },
    download_standard_report: async (payload: IFetchReportsPayload) => {
        const {roster_id, revision_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/adequacy/standard-reporting/${roster_id}/${revision_id}`,
            rest,
            signal,
            [{'Content-Type': 'text/csv'}],
            undefined,
            true,
        );
    },
    fetch_specialty_grouping: async (payload: IFetchSpecialtyGroupingPayload) => {
        const {roster_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/adequacy/specialty-grouping/${roster_id}`,
            rest,
            signal,
            undefined,
            undefined,
            true,
        );
    },
    fetch_max_time_and_distance: async (payload: IFetchMaxTimeDistPayload) => {
        const {roster_id, revision_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/adequacy/max_time_and_distance/${roster_id}/${revision_id}`,
            rest,
            signal,
            undefined,
            undefined,
            true,
        );
    },
    fetch_avg_time_and_distance: async (payload: IFetchAvgTimeDistPayload) => {
        const {roster_id, revision_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/adequacy/avg_time_and_distance/${roster_id}/${revision_id}`,
            rest,
            signal,
            undefined,
            undefined,
            true,
        );
    },
    fetch_specialty_stds_by_county: async (payload: IFetchSpecialtyStdsByCountyPayload) => {
        const {roster_id, revision_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/adequacy/specialty_code_by_county/${roster_id}/${revision_id}`,
            rest,
            signal,
            undefined,
            undefined,
            true,
        );
    },
    fetch_specialty_stds_by_county_batch: async (
        payload: IFetchSpecialtyStdsByCountyBatchPayload,
    ) => {
        const {roster_id, revision_id, signal, ...rest} = payload;
        return await post(
            `/v2/network-optimization/adequacy/specialty_code_by_county/batch/${roster_id}/${revision_id}`,
            rest,
            signal,
            undefined,
            undefined,
            true,
        );
    },
    download_compliance_summary: async (
        runId: number,
        revisionId: number,
        filters: IGeographyFilterPayload,
        distanceCalcType: string,
        signal: AbortSignal,
    ) => {
        return await post(
            `/v2/network-optimization/adequacy/specialty/${runId}/${revisionId}`,
            {...filters, distance_calc_type: distanceCalcType},
            signal,
            [{'Content-Type': 'text/csv'}],
            undefined,
            true,
        );
    },
    downloadErrorReport: (fileUploadId: number, signal?: AbortSignal): Promise<Response> =>
        streamGetRequest(`/v2/network-optimization/files/${fileUploadId}/error-report`, signal),
    // File management endpoints
    listFiles: async (page = 1, pageSize = 20, signal?: AbortSignal) => {
        return await get(`/v2/network-optimization/files?page=${page}&page_size=${pageSize}`, signal, undefined, true);
    },
    getFileDetail: async (fileId: number, signal?: AbortSignal) => {
        return await get(`/v2/network-optimization/files/${fileId}`, signal, undefined, true);
    },
    getFileUploadStatus: async (fileId: string, signal?: AbortSignal) => {
        return await get(`/v2/network-optimization/files/${fileId}/status`, signal, undefined, true);
    },
    initUpload: async (filename: string, fileSize: number, signal: AbortSignal): Promise<{upload_id: string}> =>
        post('/v2/network-optimization/files/upload/init', {filename, file_size: fileSize}, signal, undefined, undefined, true),
    continueProcessing: async (fileId: string, signal: AbortSignal) =>
        post(`/v2/network-optimization/files/${fileId}/continue-processing`, {}, signal, undefined, undefined, true),
    initiateResubmit: async (fileId: string, filename: string, fileSize: number, signal: AbortSignal): Promise<{upload_id: string}> =>
        post(`/v2/network-optimization/files/${fileId}/resubmit`, {filename, file_size: fileSize}, signal, undefined, undefined, true),
    uploadChunk: async (uploadId: string, chunkNumber: number, chunk: ArrayBuffer, signal: AbortSignal): Promise<{upload_id: string; chunk_number: number}> =>
        putBinaryData(`/v2/network-optimization/files/upload/${uploadId}/chunks/${chunkNumber}`, chunk, signal, true),
    completeUpload: async (uploadId: string, totalChunks: number, signal: AbortSignal): Promise<{upload_id: string; status: string}> =>
        post(`/v2/network-optimization/files/upload/${uploadId}/complete`, {total_chunks: totalChunks}, signal, undefined, undefined, true),
    getUploadStatus: async (uploadId: string, signal: AbortSignal): Promise<{upload_id: string; status: 'assembling' | 'uploaded' | 'failed'; filename: string; size: number}> =>
        get(`/v2/network-optimization/files/upload/${uploadId}/status`, signal, undefined, true),
};
export {
    analytics,
    savedMarkets,
    quantum,
    accessRoles,
    modules,
    users,
    organizations,
    dashboards,
    mapConfigs,
    visualizations,
    userDatasets,
    datasetPermissions,
    savedQuery,
    hcIndex,
    insightContainer,
    canvas,
    ffEnv,
    download,
    search,
    siteSearch,
    siteConfig,
    contentAccess,
    geography,
    determineAPIHost,
    content,
    networkOptimization,
};
